import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import { HiArrowLeft } from 'react-icons/hi';
import { ProductCard, Section, SectionHeader, reveal } from '../../components';
import data from '../../data/index.json';

const Store = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const products = data.products || [];

  // The back affordance only makes sense on the standalone /store route.
  const isStandalone = location.pathname.startsWith('/store');

  const categories = [
    { key: 'all', label: t('All Products') },
    { key: 'apparel', label: t('Apparel') },
  ];

  const filteredProducts =
    selectedCategory === 'all'
      ? products
      : products.filter((p) => p.category === selectedCategory);

  return (
    <Section id="store" tone="paper">
      {isStandalone && (
        <Link to="/" className="link-underline text-sm font-medium mb-10 inline-flex">
          <HiArrowLeft aria-hidden />
          {t('Back')}
        </Link>
      )}

      <SectionHeader
        index="10"
        label={t('Hyphen Merch')}
        title={
          <>
            {t('The Hyphen')} <em>{t('Store')}</em>
          </>
        }
        lede={t(
          'Premium quality apparel and branded merchandise — designed for innovators.'
        )}
        aside={
          <div className="flex items-center gap-6">
            {categories.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                onClick={() => setSelectedCategory(key)}
                aria-pressed={selectedCategory === key}
                className={`label pb-1.5 border-b transition-colors duration-200 ${
                  selectedCategory === key
                    ? 'text-ink border-gold'
                    : 'border-transparent hover:text-ink'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        }
      />

      <AnimatePresence mode="wait">
        <motion.div
          key={selectedCategory}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14 mt-14 md:mt-20"
        >
          {filteredProducts.map((product, index) => (
            <motion.div key={product.id} {...reveal(index)}>
              <ProductCard product={product} />
            </motion.div>
          ))}
        </motion.div>
      </AnimatePresence>

      {filteredProducts.length === 0 && (
        <p className="mt-16 py-16 text-center text-ink-faint border-t border-[color:var(--rule)]">
          {t('No products found in this category.')}
        </p>
      )}

      <p className="label mt-16 pt-6 border-t border-[color:var(--rule)]">
        {t('All products are made to order and shipped within 5 business days')}
      </p>
    </Section>
  );
};

export default Store;
